/**
 * Layer 4 — AnalysisEngine  (audited: Sheet28 layer)
 * ─────────────────────────────────────────────────────────────────────────────
 * Deterministic implementation of the SECONDARY business calculations:
 * lookup, aggregation, scoring and conditional logic derived from Sheet28.
 *
 * Contract:
 *   runAnalysisEngine(coreOutput, referenceData) -> AnalysisOutput
 *
 * Rules:
 *   - No randomness, no AI.
 *   - Only reads data from CoreOutput and referenceData tables.
 *   - MOCK path surfaces existing sample structures for the UI to render.
 *   - FUTURE_V299_RULE: throws until Golden Master rules are supplied.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { SOURCE_STATUS, ENGINE_VERSION } from "./sourceStatus";
import { MOCK_VARIANTS } from "@/lib/mockProfiles";

/**
 * @typedef {Object} AnalysisOutput
 * @property {"MOCK_POC"|"VALIDATED_V299"} source
 * @property {string} engineVersion
 * @property {Object} scores            aggregated scores (Sheet28 output shape TBD)
 * @property {Object} categorized       category assignments (e.g., trait/wealth axes)
 * @property {Object} rawSections       raw section payloads for InterpretationEngine
 */

const NOT_IMPLEMENTED_V299 =
  "[AnalysisEngine · V2.9.9] Not implemented yet. This layer corresponds to the " +
  "audited Sheet28 lookup/aggregation/scoring stage and must be migrated 1:1 " +
  "from the validated Golden Master. Do NOT invent rules.";

// ─────────────────────────── MOCK PATH (PoC only) ───────────────────────────

/**
 * MOCK analysis. Fetches the pre-authored variant payload identified by
 * `coreOutput.mockVariantId` and passes it through unchanged. This is NOT a
 * Life Blueprint calculation.
 *
 * @param {import('./coreRuleEngine').CoreOutput} coreOutput
 * @param {typeof import('./referenceData').referenceData} _refData
 * @returns {AnalysisOutput}
 */
export function runAnalysisEngineMock(coreOutput, _refData) {
  const variant = MOCK_VARIANTS.find((v) => v.id === coreOutput.mockVariantId) || MOCK_VARIANTS[0];
  return {
    source: SOURCE_STATUS.MOCK_POC,
    engineVersion: ENGINE_VERSION.POC_MOCK,
    scores: {}, // MOCK: not computed
    categorized: {
      variantId: variant.id,
      variantLabel: variant.label,
    },
    rawSections: {
      summaryCards: variant.summaryCards,
      overviewMetrics: variant.overviewMetrics,
      themes: variant.themes,
      numbers: variant.numbers,
      wealth: variant.wealth,
    },
  };
}

// ─────────────────────────── FUTURE_V299_RULE ────────────────────────────────

/**
 * FUTURE_V299_RULE — reserved.
 *
 * Implementation guidance (do NOT execute yet):
 *   1. Consume `coreOutput.rawFactors`.
 *   2. Apply Sheet28 lookups via referenceData.getTable("R-ANALYSIS-LOOKUPS")
 *      and referenceData.getTable("R-ANALYSIS-THRESHOLDS").
 *   3. Produce `scores` and `categorized` per Sheet28 semantics.
 *   4. Populate `rawSections` with the exact numeric/categorical values
 *      that InterpretationEngine needs (no free text at this layer).
 *   5. Return with `source: VALIDATED_V299` and `engineVersion: V2.9.9`.
 *
 * @param {import('./coreRuleEngine').CoreOutput} coreOutput
 * @param {typeof import('./referenceData').referenceData} referenceData
 * @returns {AnalysisOutput}
 */
export function runAnalysisEngineV299(coreOutput, referenceData) {
  // eslint-disable-line no-unused-vars
  throw new Error(NOT_IMPLEMENTED_V299);
}
